import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Scrollbars from 'react-custom-scrollbars-2';
import IntlMessages from 'Util/IntlMessages';
import icError from 'Assets/img/ic_error_16_danger@2x.png';
import icInfo from 'Assets/img/ic_info_16_basic@2x.png';
import icArrowDown from 'Assets/img/ic_arrow_down_24_strong@2x.png';
import icArrowDownDisabled from 'Assets/img/ic_arrow_down_24_disabled@2x.png';
import icSelect from 'Assets/img/ic_select_20_primary@2x.png';

const BasicSelectInput = ({
  titleId,
  placeholderId,
  hintId,
  value,
  onChange,
  isError = false,
  titleStyle,
  menuItems,
  isGroup = false,
  isMultiple = false,
  disabled = false,
  size = 'medium',
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isDanger, setIsDanger] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = event => {
      if (!isOpen) return;
      if (event.target.closest('.basic-input-select-selected-menu-item')) return;
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
      // 드롭다운이 열려있을 때는 modal의 외부 클릭 이벤트를 막음
      event.stopPropagation();
      event.preventDefault();
      setIsOpen(false);
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // open 상태가 변경될 때마다 hasBeenOpened와 isDanger 업데이트
  useEffect(() => {
    if (isOpen) {
      setHasBeenOpened(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (hasBeenOpened && !isOpen) {
      // 한번 열린 이후 닫혔을 때만 isDanger 상태 업데이트
      setIsDanger(isError);
    }
  }, [isOpen, isError, hasBeenOpened]);

  // 드롭다운 위치 계산
  const calculateDropdownPosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const top = rect.top + window.scrollY + (size === 'small' ? 40 : 52);
      const left = rect.left + window.scrollX;
      setDropdownPosition({ top, left });
    }
  };

  // 드롭다운이 열릴 때 위치 계산
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen]);

  // 스크롤 이벤트 감지하여 위치 재계산
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => {
      calculateDropdownPosition();
    };

    // window 스크롤 이벤트 리스너 추가
    window.addEventListener('scroll', handleScroll, true);

    // 모든 스크롤 가능한 부모 요소들에 대한 스크롤 이벤트 리스너 추가
    const scrollableParents = [];
    let parent = dropdownRef.current?.parentElement;

    while (parent && parent !== document.body) {
      const style = window.getComputedStyle(parent);
      if (style.overflow === 'auto' || style.overflow === 'scroll' || style.overflowY === 'auto' || style.overflowY === 'scroll') {
        scrollableParents.push(parent);
        parent.addEventListener('scroll', handleScroll, true);
      }
      parent = parent.parentElement;
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      scrollableParents.forEach(parent => {
        parent.removeEventListener('scroll', handleScroll, true);
      });
    };
  }, [isOpen]);

  const getMenuItem = () => {
    let _menuItems = isGroup ? menuItems.flat() : menuItems;

    // menuItems가 빈 배열이거나 유효하지 않은 경우
    if (!_menuItems || _menuItems.length === 0) {
      return <IntlMessages id={placeholderId} />;
    }

    if (value !== null && value !== undefined) {
      const foundItem = _menuItems.find(item => item.value === value);
      return foundItem ? foundItem.label : <IntlMessages id={placeholderId} />;
    }

    return <IntlMessages id={placeholderId} />;
  };

  const renderValue = () => (
    <div
      className={`basic-input-select-value ${(value === null || value === undefined) && 'placeholder'} ${isOpen ? 'focused' : isDanger && 'danger'} ${disabled && 'disabled'}`}
      onClick={() => !disabled && setIsOpen(!isOpen)}
    >
      {getMenuItem()}
      <img src={disabled ? icArrowDownDisabled : icArrowDown} alt='icArrowDown' style={{ width: 24, height: 24 }} />
    </div>
  );

  const renderMenuItem = item => (
    <div
      key={item.value}
      className={`basic-input-select-selected-menu-item ${size !== 'medium' && size} cursor-pointer no-drag`}
      onClick={() => {
        onChange({ target: { value: item.value } });
        if (!isMultiple) {
          setIsOpen(false);
        }
      }}
    >
      {item.value === value ? <img src={icSelect} alt='icSelect' style={{ width: '20px', height: '20px' }} /> : <div style={{ width: '20px', height: '20px' }} />}
      {item.label}
    </div>
  );

  // 동적 높이 계산
  const getDropdownHeight = () => {
    if (!menuItems || menuItems.length === 0) return '40px';

    // 실제 아이템 높이를 측정하기 위해 임시로 렌더링
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.pointerEvents = 'none';
    tempDiv.className = 'basic-input-select-selected';
    document.body.appendChild(tempDiv);

    let totalHeight = 0;

    if (isGroup) {
      // 그룹인 경우
      menuItems.forEach((group, groupIndex) => {
        if (Array.isArray(group)) {
          group.forEach((item, itemIndex) => {
            const itemDiv = document.createElement('div');
            itemDiv.className = 'basic-input-select-selected-menu-item cursor-pointer no-drag';
            itemDiv.style.padding = '8px 12px';
            itemDiv.style.borderBottom = '1px solid var(--Border-Default-1-Rest, #f0f0f0)';
            itemDiv.style.display = 'flex';
            itemDiv.style.alignItems = 'center';
            itemDiv.style.gap = '8px';
            itemDiv.innerHTML = `
              <div style="width: 20px; height: 20px;"></div>
              <span>${item.label?.props?.id ? item.label.props.id : item.label || ''}</span>
            `;
            tempDiv.appendChild(itemDiv);
          });

          // 그룹 간 구분선 높이 추가 (첫 번째 그룹 제외)
          if (groupIndex > 0) {
            totalHeight += 3; // border-top 높이
          }
        }
      });
    } else {
      // 일반 배열인 경우
      menuItems.forEach(item => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'basic-input-select-selected-menu-item cursor-pointer no-drag';
        itemDiv.style.padding = '8px 12px';
        itemDiv.style.borderBottom = '1px solid var(--Border-Default-1-Rest, #f0f0f0)';
        itemDiv.style.display = 'flex';
        itemDiv.style.alignItems = 'center';
        itemDiv.style.gap = '8px';
        itemDiv.innerHTML = `
          <div style="width: 20px; height: 20px;"></div>
          <span>${item.label?.props?.id ? item.label.props.id : item.label || ''}</span>
        `;
        tempDiv.appendChild(itemDiv);
      });
    }

    // 실제 높이 측정
    totalHeight = tempDiv.offsetHeight;

    // 임시 요소 제거
    document.body.removeChild(tempDiv);

    // 최소 40px, 최대 240px로 제한
    const calculatedHeight = Math.max(40, Math.min(200, totalHeight));
    return `${calculatedHeight}px`;
  };

  const renderMenuItems = () => {
    // menuItems가 빈 배열이거나 유효하지 않은 경우
    if (!menuItems || menuItems.length === 0) {
      return null;
    }

    if (isGroup) {
      return menuItems.map((group, index) => {
        return (
          <div key={index} style={{ borderTop: index !== 0 ? '3px solid var(--Border-Default-1-Rest, #f0f0f0)' : 'none' }} className='basic-input-select-selected-menu-group cursor-pointer no-drag'>
            {group.map(renderMenuItem)}
          </div>
        );
      });
    }
    return menuItems.map(renderMenuItem);
  };

  return (
    <div {...rest} className={`basic-input`}>
      {titleId && (
        <div style={titleStyle} className='basic-input-title'>
          <IntlMessages id={titleId} />
        </div>
      )}
      <div ref={dropdownRef} className={`basic-input-select cursor-pointer no-drag ${size !== 'medium' && size}`}>
        {renderValue()}
        {isOpen &&
          createPortal(
            <div
              style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                zIndex: 9999,
                position: 'absolute',
                width: dropdownRef.current ? dropdownRef.current.offsetWidth : '100%',
                maxHeight: getDropdownHeight(),
              }}
              className='basic-input-select-selected'
            >
              <Scrollbars
                autoHide
                autoHideDuration={100}
                style={{
                  height: getDropdownHeight(),
                }}
              >
                {renderMenuItems()}
              </Scrollbars>
            </div>,
            document.body
          )}
      </div>
      {hintId && (
        <div className={`basic-input-hint ${isDanger && !isOpen && 'danger'}`}>
          <img src={isDanger ? icError : icInfo} alt='info' style={{ width: 16, height: 16 }} />
          <div className={`basic-input-hint-text ${isDanger && !isOpen && 'danger'}`}>
            <IntlMessages id={hintId} />
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicSelectInput;
